import React from 'react';
import {
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { ViewerToolbarProps } from '../../types/viewer.types';

/**
 * Toolbar component for file viewers with zoom, download, and fullscreen controls
 */
const ViewerToolbar: React.FC<ViewerToolbarProps> = ({
  fileName,
  displayName,
  fileSize,
  contentType,
  zoom,
  canZoomIn,
  canZoomOut,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onDownload,
  onFullscreen,
  showDownload = true,
  showFullscreen = true,
  additionalActions,
}) => {
  // Use displayName if provided, otherwise fall back to fileName
  const nameToDisplay = displayName || fileName;
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'K', 'M', 'G'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (bytes / Math.pow(k, i)).toFixed(1) + sizes[i];
  };

  const getContentTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
    switch (type) {
      case 'document': return 'primary';
      case 'image': return 'info';
      case 'video': return 'secondary';
      case 'audio': return 'warning';
      case 'text': return 'success';
      default: return 'primary';
    }
  };

  return (
    <Toolbar
      variant="dense"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
        minHeight: '48px !important',
        px: 2,
        gap: 1,
      }}
    >
      {/* File Info Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '300px',
          }}
          title={nameToDisplay}
        >
          {nameToDisplay}
        </Typography>

        <Chip
          label={contentType}
          size="small"
          color={getContentTypeColor(contentType)}
          variant="outlined"
          sx={{ height: '20px', fontSize: '0.7rem' }}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 1 }}
        >
          {formatFileSize(fileSize)}
        </Typography>
      </Box>

      {/* Zoom Controls - only show for image viewer (the only type wired to outer zoom state) */}
      {contentType === 'image' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Zoom Out">
            <span>
              <IconButton
                size="small"
                onClick={onZoomOut}
                disabled={!canZoomOut}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            size="small"
            variant="text"
            onClick={onResetZoom}
            sx={{
              minWidth: '60px',
              fontSize: '0.75rem',
              px: 1,
              py: 0.5,
              height: '28px',
            }}
          >
            {Math.round(zoom * 100)}%
          </Button>

          <Tooltip title="Zoom In">
            <span>
              <IconButton
                size="small"
                onClick={onZoomIn}
                disabled={!canZoomIn}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Additional Actions */}
      {additionalActions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {additionalActions}
        </Box>
      )}

      {/* Standard Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {showDownload && (
          <Tooltip title="Download File">
            <IconButton size="small" onClick={onDownload} color="primary">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {showFullscreen && (
          <Tooltip title="Fullscreen">
            <IconButton size="small" onClick={onFullscreen} color="primary">
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Toolbar>
  );
};

export default ViewerToolbar;